/** ************************************************************************
 * Atomic Component for the bPerx iFrame UI.
 *
 * Atomic Type: Offer, Display Control, Button
 * Purpose: Control whether or not the offer is displayed, via a button
 *
 * This button will toggle between the "display/hidden" states.
 *
 * @file   Atomic Component, offer display control, button
 * @author Paul J Stales
 * @since  2.2.0
 ************************************************************************ */

import { FunctionalComponent, h } from 'preact'
import { css } from 'aphrodite'
import { IFRAME_CLASSNAMES } from '../../../../../../common/constants/ConstantsIFrameClassNames'
import { DisplayEvent } from '../../../../../../common/types/reportableEvents/subTypes/DisplayEvent'
import { TransactionInformation } from '../../../../../../common/types/transactionInformationTypes/TransactionInformation'
import { ReportableEventType } from '../../../../../../common/types/reportableEvents/ReportableEvent'

/**
 * Props Interface for Offer Change Selection Button
 */
interface OfferChangeSelectionButtonProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles: { [key: string]: any }
  numberOfProductsInOffer?: number
  areProductsVisible: boolean
  setProductsVisible: (areProductsVisible: boolean) => void
  transactionInformation: TransactionInformation
  setTransactionInformation: (
    transactionInformation: TransactionInformation
  ) => void
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
  displayEvent: DisplayEvent
  setDisplayEvent: (displayEvent: DisplayEvent) => void
  displayEventHandler: (
    displayEvent: DisplayEvent,
    setDisplayEvent: (displayEvent: DisplayEvent) => void,
    newTransactionInformation: TransactionInformation,
    reportableEventType: string
  ) => void
}

/**
 * Component "Offer Change Selection Button" toggles the visibility
 * of the items being offered, allowing you to change your selection.
 *
 * The button has two states, either "change my selection" (which
 * will then show the items being offered), and "back" (which will
 * stop showing the items being offered.)
 *
 * @param OfferChangeSelectionButtonProps
 * @returns
 */
const OfferChangeSelectionButton: FunctionalComponent<
  OfferChangeSelectionButtonProps
> = ({
  styles,
  numberOfProductsInOffer,
  areProductsVisible,
  setProductsVisible,
  transactionInformation,
  setTransactionInformation,
  setDoesPartnerNeedUpdateForTransactionInformation,
  displayEvent,
  setDisplayEvent,
  displayEventHandler
}) => {
  return (
    <div
      class={`${
        IFRAME_CLASSNAMES.BPERX_CHANGE_SELECTION_BUTTON_CONTAINER
      }${' '}${
        styles[IFRAME_CLASSNAMES.BPERX_CHANGE_SELECTION_BUTTON_CONTAINER] ===
        undefined
          ? ''
          : css(
              styles[IFRAME_CLASSNAMES.BPERX_CHANGE_SELECTION_BUTTON_CONTAINER]
            )
      }`}
    >
      {numberOfProductsInOffer !== undefined && numberOfProductsInOffer > 1 && (
        <button
          class={`${IFRAME_CLASSNAMES.BPERX_CHANGE_SELECTION_BUTTON}${' '}${
            styles[IFRAME_CLASSNAMES.BPERX_CHANGE_SELECTION_BUTTON] ===
            undefined
              ? ''
              : css(styles[IFRAME_CLASSNAMES.BPERX_CHANGE_SELECTION_BUTTON])
          }`}
          onClick={(e: Event) => {
            setProductsVisible(!areProductsVisible)

            // only update TI once, the first time they view the products
            if (
              transactionInformation.customerChoice.didViewProducts === false
            ) {
              const newTransactionInformation = transactionInformationHandler(
                transactionInformation,
                setTransactionInformation,
                setDoesPartnerNeedUpdateForTransactionInformation
              )

              displayEventHandler(
                displayEvent,
                setDisplayEvent,
                newTransactionInformation,
                ReportableEventType.DISPLAY_CUSTOMER_PRODUCT_VIEW_EVENT
              )
            }
          }}
        >
          {areProductsVisible ? '< Back' : 'Change My Selection'}
        </button>
      )}
    </div>
  )
}

export default OfferChangeSelectionButton

/**
 * Create a new TransactionInformation with the changes to didViewProducts,
 * then execute setTransactionInformation useState update function.
 *
 * There is a useEffect triggered by this that will update the
 * parent/partner window about the updated TransactionInformation.
 *
 * @param transactionInformation
 * @param setTransactionInformation
 * @param setDoesPartnerNeedUpdateForTransactionInformation
 */
function transactionInformationHandler(
  transactionInformation: TransactionInformation,
  setTransactionInformation: (
    transactionInformation: TransactionInformation
  ) => void,
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
): TransactionInformation {
  const NEW_TRANSACTION_INFORMATION = {
    ...transactionInformation
  }
  NEW_TRANSACTION_INFORMATION.customerChoice.didViewProducts = true
  setTransactionInformation(NEW_TRANSACTION_INFORMATION)
  setDoesPartnerNeedUpdateForTransactionInformation(true)
  return NEW_TRANSACTION_INFORMATION
}
